import React from 'react';
import {
  Box,
  Flex,
  HStack,
  Text,
  Avatar,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Badge,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';
import { Bell, Settings, User, LogOut, Search } from 'lucide-react';

interface NexusAdminHeaderProps {
  title: string;
  userName?: string;
  userRole?: string;
  notificationCount?: number;
}

export const NexusAdminHeader: React.FC<NexusAdminHeaderProps> = ({
  title,
  userName = "Dr. Sarah Chen",
  userRole = "Data Science Lead",
  notificationCount = 3
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Box 
      bg={bgColor} 
      px={6} 
      py={4} 
      borderBottom="1px" 
      borderColor={borderColor}
      position="sticky"
      top={0}
      zIndex={10}
    >
      <Flex justify="space-between" align="center">
        <Text fontSize="xl" fontWeight="bold" color="methodist.blue">
          {title}
        </Text>

        <HStack spacing={6}>
          {/* Search */}
          <IconButton
            aria-label="Search"
            icon={<Search size={20} />}
            variant="ghost"
            colorScheme="gray"
          />

          {/* Notifications */}
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="Notifications"
              icon={
                <Box position="relative">
                  <Bell size={20} />
                  {notificationCount > 0 && (
                    <Badge
                      position="absolute"
                      top="-2px"
                      right="-2px"
                      colorScheme="red"
                      borderRadius="full"
                      minW={5}
                      h={5}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {notificationCount}
                    </Badge>
                  )}
                </Box>
              }
              variant="ghost"
              colorScheme="gray"
            />
            <MenuList>
              <MenuItem>New model deployment ready</MenuItem>
              <MenuItem>Data pipeline alert</MenuItem>
              <MenuItem>System update available</MenuItem>
            </MenuList>
          </Menu>

          {/* Settings */}
          <IconButton
            aria-label="Settings"
            icon={<Settings size={20} />}
            variant="ghost"
            colorScheme="gray"
          />

          <Divider orientation="vertical" h="24px" />

          {/* User Menu */}
          <Menu>
            <MenuButton>
              <HStack spacing={3}>
                <Avatar 
                  size="sm" 
                  name={userName}
                  src="/assets/images/avatars/data-scientist.jpg"
                />
                <Box textAlign="left">
                  <Text fontWeight="medium" fontSize="sm">{userName}</Text>
                  <Text fontSize="xs" color="gray.500">{userRole}</Text>
                </Box>
              </HStack>
            </MenuButton>
            <MenuList>
              <MenuItem icon={<User size={16} />}>Profile</MenuItem>
              <MenuItem icon={<Settings size={16} />}>Settings</MenuItem>
              <MenuItem icon={<LogOut size={16} />}>Logout</MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>
    </Box>
  );
};
